import type { PartialDeep } from 'type-fest';
import { trackError } from '#api/errors.ts';
import type { ConfigOverride, LocalAllow } from '#dn-types/host.ts';
import { LOGGING_TIMESTAMP_ITEMS } from '#schemas/config-override/helpers/enums.ts';
import type { ConfigOverridesTransformedFormData } from '../../host-form-types.ts';
import { stringToList } from './stringToList.ts';

export function mapFormToOverrides(data: PartialDeep<ConfigOverridesTransformedFormData>): ConfigOverride[] {
  const overrides: ConfigOverride[] = [];
  const outerEntries = Object.keys(data) as Array<keyof typeof data>;
  outerEntries.forEach((key) => {
    if (key === 'preferred_ranges') {
      const value = stringToList(data[key]);
      if (value.length) {
        overrides.push({ key, value });
      }
    } else if (key === 'routines') {
      const value = data[key];
      if (value !== null) {
        overrides.push({ key, value: Number(value) });
      }
    } else if (key === 'definednet' && data.definednet?.dns) {
      const dns = data.definednet.dns;
      const resolverAddrs = stringToList(dns.resolver_addrs);
      if (resolverAddrs.length) {
        overrides.push({ key: 'definednet.dns.resolver_addrs', value: resolverAddrs });
      }
      const matchDomains = stringToList(dns.match_domains);
      if (matchDomains.length) {
        overrides.push({ key: 'definednet.dns.match_domains', value: matchDomains });
      }
      const searchDomains = stringToList(dns.search_domains);
      if (searchDomains.length) {
        overrides.push({ key: 'definednet.dns.search_domains', value: searchDomains });
      }
    } else if (key === 'lighthouse' && data.lighthouse) {
      if (data.lighthouse.remote_allow_list?.length) {
        const value = data.lighthouse.remote_allow_list.reduce<Record<string, boolean>>((carry, row) => {
          carry[row.cidr] = row.allow;
          return carry;
        }, {});
        overrides.push({ key: 'lighthouse.remote_allow_list', value });
      }
      if (data.lighthouse.local_allow_list?.length) {
        const value = data.lighthouse.local_allow_list.reduce<LocalAllow>(
          (carry, row) => {
            if ('interface' in row && row.interface) {
              carry.interfaces = { ...carry.interfaces, [row.interface]: row.allow };
            } else if ('cidr' in row) {
              carry[row.cidr] = row.allow;
            }
            return carry;
          },
          { interfaces: {} }
        );
        overrides.push({ key: 'lighthouse.local_allow_list', value });
      }
      if (data.lighthouse.advertise_addrs) {
        const value = stringToList(data.lighthouse.advertise_addrs);
        if (value.length) {
          overrides.push({ key: 'lighthouse.advertise_addrs', value });
        }
      }
      if (data.lighthouse.interval !== undefined && data.lighthouse.interval !== null) {
        overrides.push({ key: 'lighthouse.interval', value: data.lighthouse.interval });
      }
      if (data.lighthouse.serve_dns === 'On') {
        overrides.push({ key: 'lighthouse.serve_dns', value: true });

        const dnsHost = data.lighthouse.dns?.host;
        if (dnsHost && dnsHost !== '') {
          overrides.push({ key: 'lighthouse.dns.host', value: dnsHost });
        }
        // TODO: remove this check once we're confident it's not a problem.  I just don't trust our types completely yet.
        if (typeof data.lighthouse.dns?.port !== 'number') {
          trackError('Somehow dns port is not a number');
        } else {
          overrides.push({ key: 'lighthouse.dns.port', value: data.lighthouse.dns.port });
        }
      } else if (data.lighthouse.serve_dns === 'Off') {
        overrides.push({ key: 'lighthouse.serve_dns', value: false });
      }
    } else if (key === 'listen' && data.listen) {
      if (data.listen.host) {
        overrides.push({ key: 'listen.host', value: data.listen.host });
      }
      if (data.listen.batch !== undefined && data.listen.batch !== null) {
        overrides.push({ key: 'listen.batch', value: data.listen.batch });
      }
      if (data.listen.read_buffer !== undefined && data.listen.read_buffer !== null) {
        overrides.push({ key: 'listen.read_buffer', value: data.listen.read_buffer });
      }
      if (data.listen.write_buffer !== undefined && data.listen.write_buffer !== null) {
        overrides.push({ key: 'listen.write_buffer', value: data.listen.write_buffer });
      }
      if (data.listen.udp_offloads === 'True') {
        overrides.push({ key: 'listen.udp_offloads', value: true });
      } else if (data.listen.udp_offloads === 'False') {
        overrides.push({ key: 'listen.udp_offloads', value: false });
      }
    } else if (key === 'logging' && data.logging) {
      if (data.logging.format) {
        overrides.push({ key: 'logging.format', value: data.logging.format });
      }
      if (data.logging.level) {
        overrides.push({ key: 'logging.level', value: data.logging.level });
      }
      if (data.logging.timestamp_format) {
        const logging = data.logging;
        const timestampFormat = LOGGING_TIMESTAMP_ITEMS.find((i) => i.label === logging.timestamp_format);
        if (timestampFormat) {
          overrides.push({ key: 'logging.timestamp_format', value: timestampFormat.value });
        }
      }
      if (Object.keys(data.logging).length > 3) {
        throw new Error('Unexpected form data');
      }
    } else if (key === 'stats' && data.stats) {
      if (data.stats.type) {
        overrides.push({ key: 'stats.type', value: data.stats.type });

        if (data.stats.interval) {
          overrides.push({ key: 'stats.interval', value: data.stats.interval });
        }
        if (data.stats.lighthouse_metrics === 'On') {
          overrides.push({ key: 'stats.lighthouse_metrics', value: true });
        }
        if (data.stats.lighthouse_metrics === 'Off') {
          overrides.push({ key: 'stats.lighthouse_metrics', value: false });
        }
        if (data.stats.message_metrics === 'On') {
          overrides.push({ key: 'stats.message_metrics', value: true });
        }
        if (data.stats.message_metrics === 'Off') {
          overrides.push({ key: 'stats.message_metrics', value: false });
        }

        if (data.stats.type === 'graphite') {
          if (data.stats.host) {
            overrides.push({ key: 'stats.host', value: data.stats.host });
          }
          if (data.stats.prefix) {
            overrides.push({ key: 'stats.prefix', value: data.stats.prefix });
          }
        }

        if (data.stats.type === 'prometheus') {
          if (data.stats.namespace) {
            overrides.push({ key: 'stats.namespace', value: data.stats.namespace });
          }
          if (data.stats.subsystem) {
            overrides.push({ key: 'stats.subsystem', value: data.stats.subsystem });
          }
          if (data.stats.listen) {
            overrides.push({ key: 'stats.listen', value: data.stats.listen });
          }
          if (data.stats.path) {
            overrides.push({ key: 'stats.path', value: data.stats.path });
          }
        }
      }
      if (Object.keys(data.stats).length > 10) {
        throw new Error('Unexpected form data');
      }
    } else if (key === 'static_map' && data.static_map) {
      if (data.static_map.network) {
        overrides.push({ key: 'static_map.network', value: data.static_map.network });
      }
    } else if (key === 'punchy' && data.punchy) {
      if (data.punchy.delay) {
        overrides.push({ key: 'punchy.delay', value: data.punchy.delay });
      }
      if (data.punchy.respond_delay) {
        overrides.push({ key: 'punchy.respond_delay', value: data.punchy.respond_delay });
      }
    } else if (key === 'tun' && data.tun) {
      if (data.tun.tx_queue !== undefined && data.tun.tx_queue !== null) {
        overrides.push({ key: 'tun.tx_queue', value: data.tun.tx_queue });
      }
      if (data.tun.mtu !== undefined && data.tun.mtu !== null) {
        overrides.push({ key: 'tun.mtu', value: data.tun.mtu });
      }
      if (data.tun.dev !== undefined && data.tun.dev !== '') {
        overrides.push({ key: 'tun.dev', value: data.tun.dev });
      }
      if (data.tun.disabled === 'True') {
        overrides.push({ key: 'tun.disabled', value: true });
      } else if (data.tun.disabled === 'False') {
        overrides.push({ key: 'tun.disabled', value: false });
      }
      if (data.tun.use_offloads === 'True') {
        overrides.push({ key: 'tun.use_offloads', value: true });
      } else if (data.tun.use_offloads === 'False') {
        overrides.push({ key: 'tun.use_offloads', value: false });
      }
    }
  });

  return overrides;
}
